import { eur, num } from "@/lib/format";
import { Panel, StatusBadge, InlineWarning } from "@/components/common/Primitives";

const BASIS_LABEL = {
  CO2: "CO₂-tarief",
  CO2_PLUS_LIST: "CO₂ + catalogusprijs",
  LIST: "Catalogusprijs",
};

const summarise = (p) => {
  if (p.basis === "LIST") {
    const lp = p.list_price || {};
    return `${num(lp.pct)}% catalogusprijs · benzine ${eur(lp.petrol)}`;
  }
  const table = (p.co2_tables || {}).ALL || (p.co2_tables || {}).PETROL || [];
  if (!table.length) return "—";
  const first = table[0];
  const last = table[table.length - 1];
  const list = p.list_price ? ` + ${num(p.list_price.pct)}% catalogusprijs` : "";
  return `${table.length} schijven · vaste voet ${eur(first.base_amount)} · toptarief ${eur(
    last.rate,
    { decimals: 2 }
  )}/g vanaf ${num(last.from_co2)} g${list}`;
};

export const TariffPeriodsPanel = ({ periods = [], integrity, onReload, busy }) => (
  <Panel
    title="Officiële BPM-tarieven (1993 – 2026)"
    subtitle={integrity?.source_name}
    testid="settings-tariff-periods"
    actions={
      <button
        type="button"
        onClick={onReload}
        disabled={busy}
        data-testid="tariffs-reload"
        className="rounded-sm border border-slate-300 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
      >
        {busy ? "Bezig..." : "Officiële tarieven opnieuw laden"}
      </button>
    }
  >
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
      {integrity?.passed ? (
        <StatusBadge status="CONNECTED" label="Integriteitscheck geslaagd" />
      ) : (
        <span className="chip border-red-300 bg-red-50 text-red-800">Integriteitscheck gefaald</span>
      )}
      <span className="text-[11.5px] text-slate-600" data-testid="tariff-integrity-summary">
        {num(integrity?.checked_periods)} van {num(integrity?.official_periods)} periodes geladen ·{" "}
        {num(integrity?.reference_cases)} referentieberekeningen gecontroleerd · {integrity?.first_year}–
        {integrity?.last_year}
      </span>
      {integrity?.source_url && (
        <a
          href={integrity.source_url}
          target="_blank"
          rel="noreferrer"
          className="text-[11.5px] font-semibold text-blue-700 hover:underline"
        >
          Bron (Belastingdienst pdf)
        </a>
      )}
    </div>
    {(integrity?.errors || []).length > 0 && (
      <div className="space-y-1 p-3">
        {integrity.errors.map((e) => (
          <InlineWarning key={`${e.check}-${e.message}`} tone="danger">
            {e.check}: {e.message}
          </InlineWarning>
        ))}
      </div>
    )}
    <div className="max-h-[520px] overflow-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Periode</th>
            <th>Grondslag</th>
            <th>CO₂-norm</th>
            <th>Schijven / percentage</th>
            <th>Dieseltoeslag</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {periods
            .slice()
            .reverse()
            .map((p) => (
              <tr key={p.period_key} data-testid={`tariff-period-${p.period_key}`}>
                <td className="whitespace-nowrap text-[11.5px] font-semibold">{p.label}</td>
                <td className="text-[11px]">{BASIS_LABEL[p.basis] || p.basis}</td>
                <td className="text-[11px]">{p.co2_method}</td>
                <td className="max-w-[420px] whitespace-normal break-words text-[10.5px] leading-tight text-slate-600">
                  {summarise(p)}
                </td>
                <td className="whitespace-nowrap num text-[11px]">
                  {p.diesel_surcharge
                    ? `${eur(p.diesel_surcharge.rate, { decimals: 2 })}/g > ${num(
                        p.diesel_surcharge.threshold
                      )} g`
                    : "—"}
                </td>
                <td>
                  {p.verified ? (
                    <StatusBadge status="CONNECTED" label="Officieel" />
                  ) : (
                    <span className="chip border-amber-300 bg-amber-50 text-amber-800">
                      Te verifiëren
                    </span>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
    <p className="px-3 py-2 text-[10.5px] leading-snug text-slate-500">
      Een officiële tarieftabel betekent niet dat de BPM van een auto definitief is: de uitkomst hangt
      ook af van de betrouwbaarheid van CO₂, netto-catalogusprijs, uitvoering en afschrijvingsmethode.
      Per auto laat de BPM-dialoog zien welk tarief is gekozen en waarom.
    </p>
  </Panel>
);

export default TariffPeriodsPanel;
